import React, { useMemo } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Member } from "../../features/members/models/Member";
import {
  VisitOutcome,
} from "../../features/sessions/models/VisitOutcome";
import { SessionVisits } from "../../features/sessions/visits-slice";
import AttendanceRow from "./AttendanceRow";
import AddOneOffMemberPicker from "./AddOneOffMemberPicker";

interface OneOffMembersSectionProps {
  members: Member[];
  /** Member ids attending this occurrence one-off (not on the plan roster). */
  oneOffIds: string[];
  outcomes: SessionVisits;
  /** Member id → the name of their Visit's own Payment Type (e.g. a late-cancellation fee). */
  feeNames?: Record<string, string>;
  /** Members already on the roster or already added one-off. */
  excludedMemberIds: string[];
  onAdd: (memberId: string) => void;
  onRemove: (memberId: string) => void;
  onChange: (memberId: string, outcome: VisitOutcome) => void;
}

/**
 * The "one-off members" block of the attendance panel: members who join this
 * single occurrence only. Their Visits count toward Billing; the plan roster
 * stays untouched.
 */
const OneOffMembersSection: React.FC<OneOffMembersSectionProps> = ({
  members,
  oneOffIds,
  outcomes,
  feeNames,
  excludedMemberIds,
  onAdd,
  onRemove,
  onChange,
}) => {
  const { t } = useTranslation();

  const memberNameById = useMemo(
    () => new Map(members.map((m) => [m.id, `${m.firstName} ${m.lastName}`.trim()])),
    [members]
  );

  return (
    <div className="rounded-xl border border-grey-90 p-3 dark:border-primary-20">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-grey-10 dark:text-grey-90">
          {t("sessions.attendance.oneOffMembers")}
          <span className="ml-2 font-normal text-grey-30 dark:text-grey-70">
            {oneOffIds.length}
          </span>
        </p>
        <AddOneOffMemberPicker
          members={members}
          excludedMemberIds={excludedMemberIds}
          onAdd={onAdd}
        />
      </div>

      {oneOffIds.length > 0 && (
        <div className="mt-1 divide-y divide-grey-95 dark:divide-primary-20">
          {oneOffIds.map((memberId) => (
            <div key={memberId} className="flex items-center gap-2">
              <div className="flex-1">
                <AttendanceRow
                  name={memberNameById.get(memberId) ?? t("sessions.formerMember", "Former member")}
                  memberId={memberId}
                  outcome={outcomes[memberId] ?? VisitOutcome.Unmarked}
                  feeName={feeNames?.[memberId]}
                  onChange={(outcome) => onChange(memberId, outcome)}
                />
              </div>
              <button
                type="button"
                onClick={() => onRemove(memberId)}
                className="rounded-md p-1 text-grey-40 hover:bg-grey-95 hover:text-grey-20 dark:text-grey-60 dark:hover:bg-primary-20 dark:hover:text-white"
                title={t("sessions.attendance.removeOneOff")}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OneOffMembersSection;
